// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useLocation } from "@tanstack/react-router";
import { useMemo } from "react";
import { usePageTitle } from "@/components/layout/page-title-context";
import type { PageContext, PageContextKind } from "@/features/assistant/types";

const NO_SUBJECT = new Set(["/", "/home", "/profile", "/showcase"]);

const DETAIL: [RegExp, PageContextKind][] = [
  [/^\/chains\/(?!compare(\/|$))[^/]+$/, "chain"],
  [/^\/stores\/(?!compare(\/|$))[^/]+$/, "store"],
  [/^\/settings\/[^/]+$/, "setting"],
];

/**
 * What the assistant should treat as "this page". The label is the page title the header
 * already shows; until a page has published its title there is no context, which also keeps
 * the previous page's title from leaking onto the next route during navigation.
 */
export function derivePageContext(pathname: string, title: string | null): PageContext | null {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  if (NO_SUBJECT.has(path) || !title) return null;
  const kind = DETAIL.find(([re]) => re.test(path))?.[1] ?? "section";
  return { kind, label: title, path };
}

export function usePageContext(): PageContext | null {
  const { pathname } = useLocation();
  const title = usePageTitle()?.title ?? null;
  return useMemo(() => derivePageContext(pathname, title), [pathname, title]);
}
